/**
 * Single source of truth for "which folder does this email belong to?".
 *
 * Folder values stored on email docs (Firestore `emails.folder`):
 *   inbox | sent | drafts | junk | deleted   - Outlook well-known folders
 *   archive                                   - Outlook's Archive folder
 *   filed                                     - ANY other Outlook folder the owner filed it into
 *                                               (see outlookFolderName on the doc)
 *
 * Why "archive"/"filed" exist: a message moved out of the inbox used to be recorded as
 * deleted:true while keeping folder:'inbox' (the sync could not tell a move from a delete),
 * so it kept rendering as an inbox row and inflated unread counts.
 */
export type EmailFolder = 'inbox' | 'sent' | 'drafts' | 'junk' | 'deleted' | 'archive' | 'filed';

const KNOWN: ReadonlyArray<EmailFolder> = ['inbox', 'sent', 'drafts', 'junk', 'deleted', 'archive', 'filed'];

/** True for any value stored on `emails.folder` by the current data model. */
export const isEmailFolder = (v: unknown): v is EmailFolder =>
  typeof v === 'string' && (KNOWN as ReadonlyArray<string>).includes(v);

interface FolderFields {
  folder?: unknown;
  deleted?: unknown;
  emailType?: unknown;
  quarantined?: unknown;
}

export function normalizeEmailFolder(email: FolderFields | null | undefined): EmailFolder {
  if (!email) return 'inbox';
  const raw = email.folder;
  // deleted:true + folder:'inbox' (or no folder) is the pre-fix mislabel for "moved out of the
  // inbox": never present it as inbox, whatever the repair job has or hasn't reached yet.
  if (email.deleted === true && (raw === 'inbox' || !raw)) return 'deleted';
  if (typeof raw === 'string' && (KNOWN as ReadonlyArray<string>).includes(raw)) return raw as EmailFolder;
  if (email.deleted === true) return 'deleted';
  if (email.emailType === 'sent') return 'sent';
  if (email.quarantined === true) return 'junk';
  return 'inbox';
}

/** Mail the owner moved out of the inbox into Outlook's Archive or any other Outlook folder. */
export const isOutlookFiled = (f: EmailFolder): boolean => f === 'archive' || f === 'filed';

export const EMAIL_FOLDER_LABEL: Record<EmailFolder, string> = {
  inbox: 'inbox', sent: 'sent', drafts: 'drafts', junk: 'junk', deleted: 'deleted', archive: 'archive', filed: 'archive',
};

/** Tiny label for a row's folder tag: the real Outlook folder name for filed mail. */
export function emailFolderTag(email: FolderFields & { outlookFolderName?: unknown }): string {
  const f = normalizeEmailFolder(email);
  if (f === 'filed' && typeof email.outlookFolderName === 'string' && email.outlookFolderName.trim()) return email.outlookFolderName.trim();
  return EMAIL_FOLDER_LABEL[f];
}
